///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { GtthPreloaderModalEvent } from '../../shared/preloader-modal/preloader-modal-event';
import { GtthEventManager } from '../../services/event-manager.service';
import { GtthAccessDeniedModalService } from '../../shared/access-denied/accessdenied.service';
import { Principal } from '../../shared/auth/principal.service';

export enum HttpStatus {
    NETWORK_ERROR = 0,
    GATEWAY_TIMEOUT = 504,
    INTERNAL_SERVER_ERROR = 500,
    UNAUTHORIZED = 401,
    CONFLICT = 409,
    NOT_FOUND = 404,
    FORBIDDEN = 403,
    BAD_REQUEST = 400
}

export const IGNORE_HTTP_ERRORS = new HttpContextToken<number[]>(() => []);

@Injectable({
    providedIn: 'root'
})
export class ErrorHandlerInterceptor implements HttpInterceptor {
    constructor(
        private readonly eventManager: GtthEventManager,
        private readonly router: Router,
        private readonly accessDeniedService: GtthAccessDeniedModalService,
        private readonly principal: Principal
    ) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(req).pipe(
            tap({
                error: (error: HttpEvent<unknown>) => {
                    if (error instanceof HttpErrorResponse) {
                        this.handleError(req, error);
                    }
                }
            })
        );
    }

    private handleError(req: HttpRequest<unknown>, error: HttpErrorResponse): void {
        const ignoredHttpErrorCodes = req.context.get(IGNORE_HTTP_ERRORS);
        if (ignoredHttpErrorCodes?.includes(error.status)) {
            return; // Skip handling for this error
        }

        switch (error.status) {
            case HttpStatus.GATEWAY_TIMEOUT:
            case HttpStatus.NETWORK_ERROR:
                this.handleNetworkErrors();
                break;
            case HttpStatus.INTERNAL_SERVER_ERROR:
                this.handleInternalServerError(error);
                this.logError(error);
                break;
            case HttpStatus.UNAUTHORIZED:
                this.handleUnauthorizedError(error);
                break;
            case HttpStatus.CONFLICT:
            case HttpStatus.NOT_FOUND:
            case HttpStatus.FORBIDDEN:
                this.handleCommonHttpStatusErrors(error.status);
                this.logError(error);
                break;
            case HttpStatus.BAD_REQUEST:
                this.handleBadRequest(error);
                this.logError(error);
                break;
        }
    }

    private handleNetworkErrors(): void {
        this.eventManager.broadcast({ name: 'togglePreloaderModal', eventObject: GtthPreloaderModalEvent.hide() });
        this.accessDeniedService.open('server.not.reachable');
    }

    private handleInternalServerError(error: HttpErrorResponse): void {
        // Array of URLs where the internal server error banner should not be displayed
        const noBannerUrls: string[] = ['api/lbc/uploadRadio', 'api/lbc/uploadAntenna'];

        // Check if the error URL is in the list of URLs that should bypass the error banner
        if (noBannerUrls.some((url) => error.url.includes(url))) {
            return; // Do nothing if the URL is in the noBannerUrls array
        }

        // All another case should display the internal server error banner
        this.accessDeniedService.open('internalServerError');
    }

    private handleUnauthorizedError(error: HttpErrorResponse): void {
        if (!this.router.url.startsWith('/reset/finish')) {
            this.router.navigate(['']);
        }

        if (error.url && !error.url.includes('/api/account') && !error.url.includes('/api/authentication')) {
            this.principal.identity(true);
        }
    }

    private handleCommonHttpStatusErrors(status: HttpStatus): void {
        switch (status) {
            case HttpStatus.CONFLICT:
                this.accessDeniedService.open('error.concurrencyFailure');
                break;
            case HttpStatus.NOT_FOUND:
                this.accessDeniedService.open('url.not.found');
                break;
            case HttpStatus.FORBIDDEN:
                this.accessDeniedService.open();
                break;
        }
    }

    private handleBadRequest(error: HttpErrorResponse): void {
        this.eventManager.broadcast({ name: 'gtthApp.httpError', content: error });
    }

    private logError(error: HttpErrorResponse): void {
        console.error(`${error.name}: ${error.message}`, error);
    }
}
